import type { QueryClient } from '@tanstack/react-query';

interface NetworkInformationLike {
  saveData?: boolean;
  effectiveType?: string;
}

function constrained(): boolean {
  const connection = (navigator as unknown as { connection?: NetworkInformationLike }).connection;
  if (connection?.saveData) return true;
  if (connection?.effectiveType && ['slow-2g', '2g'].includes(connection.effectiveType)) return true;
  return !navigator.onLine;
}

async function lowBattery(): Promise<boolean> {
  const getBattery = (
    navigator as unknown as { getBattery?: () => Promise<{ level: number; charging: boolean }> }
  ).getBattery;
  if (!getBattery) return false;
  try {
    const b = await getBattery.call(navigator);
    return !b.charging && b.level < 0.15;
  } catch {
    return false;
  }
}

function idle(): Promise<void> {
  return new Promise((resolve) => {
    const ric = (
      window as unknown as { requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number }
    ).requestIdleCallback;
    if (ric) ric(() => resolve(), { timeout: 2000 });
    else setTimeout(resolve, 50);
  });
}

const priority = (key: readonly unknown[]): number => {
  const kind = key[0];
  if (kind === 'meta') return 0;
  if (kind === 'q' || kind === 'objects') return 1;
  return 2;
};

export async function silentRefreshCycle(client: QueryClient, maxConcurrent = 2): Promise<void> {
  if (constrained() || (await lowBattery())) return;
  const queries = client
    .getQueryCache()
    .getAll()
    .filter(
      (q) =>
        q.meta?.persist === true &&
        q.state.status === 'success' &&
        !q.isActive() &&
        q.state.fetchStatus === 'idle',
    )
    .sort(
      (a, b) => priority(a.queryKey) - priority(b.queryKey) || b.state.dataUpdatedAt - a.state.dataUpdatedAt,
    );
  let index = 0;
  const run = async () => {
    while (index < queries.length) {
      if (constrained()) return;
      const q = queries[index++];
      if (!q) return;
      await idle();
      await client
        .refetchQueries(
          { queryKey: q.queryKey, exact: true, type: 'inactive' },
          { throwOnError: false, cancelRefetch: false },
        )
        .catch(() => undefined);
    }
  };
  await Promise.all(Array.from({ length: maxConcurrent }, run));
}

export function startSilentRefresh(client: QueryClient, minutes: () => number): () => void {
  let stopped = false;
  let timer: ReturnType<typeof setTimeout> | null = null;
  const schedule = () => {
    if (stopped) return;
    timer = setTimeout(async () => {
      await silentRefreshCycle(client).catch(() => undefined);
      schedule();
    }, minutes() * 60_000);
  };
  schedule();
  return () => {
    stopped = true;
    if (timer) clearTimeout(timer);
  };
}
